import { FormControl, ValidationErrors } from '@angular/forms';
// Type copied locally — ValidatorConfig is not exported in @trademe/tangram public API
interface ValidatorConfig {
    message?: (() => string) | string;
    validator?: import('@angular/forms').AsyncValidatorFn | import('@angular/forms').ValidatorFn;
}
import {
    HOME_PHONE_NUMBER_REGEX,
    MOBILE_PHONE_NUMBER_REGEX,
    FREE_CALL_PHONE_NUMBER_REGEX,
    OTHER_COUNTRY_PHONE_NUMBER_REGEX
} from './phone-number.constants';

type IValidationInfo = {
    validations: {
        required: { value: boolean; errorMessage: string };
    };
};

type IValidationSkippable = {
    skipOptionsValidation?: boolean;
    skipRangeValidation?: boolean;
};

type ValueValidatorConfig = {
    validate: (value: any) => boolean;
    message: string;
};

const HOME_PHONE_NUMBER_REGEXP = new RegExp(HOME_PHONE_NUMBER_REGEX);
const MOBILE_PHONE_NUMBER_REGEXP = new RegExp(MOBILE_PHONE_NUMBER_REGEX);
const FREE_CALL_PHONE_NUMBER_REGEXP = new RegExp(FREE_CALL_PHONE_NUMBER_REGEX);
const OTHER_COUNTRY_PHONE_NUMBER_REGEXP = new RegExp(
    OTHER_COUNTRY_PHONE_NUMBER_REGEX
);
const CHAR_CODE_FOR_NEW_LINE_CHARACTER = 10;

const cachedValidators = new Map();

const absolutelyNoValue = (val: any) => val === null || val === undefined;

function required(validationInfo: any): ValueValidatorConfig {
    return {
        message:
            validationInfo.validations?.required?.errorMessage ??
            `${validationInfo.name} is required`,
        validate: (val) => {
            if (!validationInfo.validations.required.value) {
                return true;
            }
            return !absolutelyNoValue(val) && String(val).trim().length > 0;
        }
    };
}

function maxLength(validationInfo: any): ValueValidatorConfig {
    return {
        message:
            validationInfo.validations?.maxLength?.errorMessage ??
            `${validationInfo.name} cannot be longer than ${validationInfo.validations.maxLength?.value} characters`,
        validate: (val) => {
            if (
                absolutelyNoValue(validationInfo.validations.maxLength?.value)
            ) {
                return true;
            }

            return (
                absolutelyNoValue(val) ||
                val.length <=
                    getAdjustedMaxLength(
                        val,
                        validationInfo.validations.maxLength.value
                    )
            );
        }
    };
}

/**
 * We have to adjust the maxLength validation dynamically to fix a rather nasty API issue.
 * The API considers newlines to be CRLF, yet the browser and the HTML standard operate on LF (standards FTW).
 * Every newline is counted as two characters on the server, but only as one on the client.
 * In order to get the same behavior as the server in terms of validation, we need to simulate what the
 * server is doing (adding an additional character per newline) by decrementing the maxLength validation
 * value by the number of newline characters in the string. We can't just add the CR character to the string
 * as the browser will automatically replace it.
 * APITODO: This should actually be fixed in the API. Like for real.
 */
function getAdjustedMaxLength(
    description: string,
    validationMaxLength: number
): number {
    let newLineCount = 0;
    if (description && description.length <= validationMaxLength) {
        for (let i = 0; i < description.length; i++) {
            if (
                description.charCodeAt(i) === CHAR_CODE_FOR_NEW_LINE_CHARACTER
            ) {
                newLineCount++;
            }
        }
    }
    return Math.max(0, validationMaxLength - newLineCount);
}

type IValidationInfoWithOptions = {
    name?: string;
    options: {
        value: any;
        display?: string;
    }[];
};

type IOptionItem = {
    value: any;
    display?: string;
};

function options(
    validationInfo: IValidationInfo &
        IValidationInfoWithOptions &
        IValidationSkippable,
    optionItems?: IOptionItem[]
): ValueValidatorConfig {
    return {
        message:
            validationInfo.validations?.required?.errorMessage ??
            `${
                validationInfo.validations &&
                validationInfo.validations.required?.value === true
                    ? `${validationInfo.name} is required`
                    : `${validationInfo.name} is not valid`
            }`,
        validate: (val) => {
            if (
                !optionItems ||
                validationInfo.skipOptionsValidation ||
                absolutelyNoValue(val)
            ) {
                return true;
            }

            const equals = (v1: any, v2: any) =>
                v1 instanceof Object
                    ? v1.toString() === v2.toString()
                    : v1 === v2;
            const match = optionItems
                .map((o) => o.value)
                .find((v) => equals(v, val));
            return !absolutelyNoValue(match);
        }
    };
}

type IRangeValidationInfo = {
    name: string;
    exception: any;
    validations: {
        minValue: { value: number; errorMessage: string };
        maxValue: { value: number; errorMessage: string };
    };
};

function range(
    validationInfo: IRangeValidationInfo & IValidationSkippable
): ValueValidatorConfig {
    return {
        message:
            validationInfo.validations?.minValue?.errorMessage ??
            `${validationInfo.name} must be between ${(
                validationInfo.validations.minValue?.value || 0
            ).toLocaleString()} and ${(
                validationInfo.validations.maxValue?.value || 0
            ).toLocaleString()}`,
        validate: (val) => {
            if (validationInfo.skipRangeValidation) {
                return true;
            }

            const absolutelyNoRange = [
                validationInfo.validations.minValue?.value,
                validationInfo.validations.maxValue?.value,
                validationInfo.exception
            ].every(absolutelyNoValue);
            if (absolutelyNoValue(val) || absolutelyNoRange) {
                return true;
            }

            const proposed = Number(val || 0);
            if (isNaN(proposed)) {
                return false;
            }

            if (
                validationInfo.exception &&
                proposed === Number(validationInfo.exception)
            ) {
                return true;
            }

            if (
                !absolutelyNoValue(
                    validationInfo.validations.minValue?.value
                ) &&
                proposed < Number(validationInfo.validations.minValue?.value)
            ) {
                return false;
            }

            if (
                !absolutelyNoValue(
                    validationInfo.validations.maxValue?.value
                ) &&
                proposed > Number(validationInfo.validations.maxValue?.value)
            ) {
                return false;
            }

            return true;
        }
    };
}

type IIntFieldValidationInfo = {
    name: string;
    type: string;
    exception: any;
};

function naturalNumber(
    validationInfo: IIntFieldValidationInfo
): ValueValidatorConfig {
    return {
        message: `${validationInfo.name} cannot have decimal points`,
        validate: (val) => {
            if (validationInfo.type !== 'Numeric') {
                return true;
            }

            const proposed = val || 0;
            return Math.round(proposed) === Number(proposed);
        }
    };
}

function phoneRegex(validationInfo: any): ValueValidatorConfig {
    return {
        message: (<string>validationInfo.name)
            ?.toLocaleLowerCase()
            .includes('mobile')
            ? 'Please enter a valid mobile number'
            : 'Please enter a valid phone number',
        validate: (val) => {
            if (
                validationInfo.type !== 'tel' ||
                absolutelyNoValue(val) ||
                val.length === 0
            ) {
                return true;
            }

            if (validationInfo.isAllowAllTypes) {
                return (
                    FREE_CALL_PHONE_NUMBER_REGEXP.test(val) ||
                    OTHER_COUNTRY_PHONE_NUMBER_REGEXP.test(val) ||
                    MOBILE_PHONE_NUMBER_REGEXP.test(val) ||
                    HOME_PHONE_NUMBER_REGEXP.test(val)
                );
            }

            if (
                (<string>validationInfo.name)
                    ?.toLocaleLowerCase()
                    .includes('mobile')
            ) {
                return MOBILE_PHONE_NUMBER_REGEXP.test(val);
            }

            return (
                HOME_PHONE_NUMBER_REGEXP.test(val) ||
                OTHER_COUNTRY_PHONE_NUMBER_REGEXP.test(val)
            );
        }
    };
}

function regex(validationInfo: any): ValueValidatorConfig {
    return {
        message:
            validationInfo.validations?.regularExpression?.errorMessage ??
            `One or more words cannot be used for the ${validationInfo.name?.toLowerCase()}`,
        validate: (val) => {
            if (
                (validationInfo.validations &&
                    typeof validationInfo.validations.regularExpression
                        ?.value === 'undefined') ||
                String(val).trim().length === 0
            ) {
                return true;
            }
            return new RegExp(
                validationInfo.validations.regularExpression.value
            ).test(val?.toLowerCase());
        }
    };
}

type ErrorConverter = (
    k: string,
    validationInfo: any
) => ValidationErrors | null;
type ValidationConfig = {
    make: (validationInfo: any, options?: any) => ValueValidatorConfig;
    toValidationError: ErrorConverter;
};
const formatErrorDefault = (k: string, _: any) => ({ [k]: true });
const formatErrorMaxlength = (k: string, validationInfo: any) => ({
    [k]: { requiredLength: validationInfo.validations.maxLength?.value }
});
const validators: Record<string, ValidationConfig> = {
    required: {
        make: required,
        toValidationError: formatErrorDefault
    },
    maxlength: {
        make: maxLength,
        toValidationError: formatErrorMaxlength
    },
    natural: {
        make: naturalNumber,
        toValidationError: formatErrorDefault
    },
    range: {
        make: range,
        toValidationError: formatErrorDefault
    },
    options: {
        make: options,
        toValidationError: formatErrorDefault
    },
    phoneRegex: {
        make: phoneRegex,
        toValidationError: formatErrorDefault
    },
    regex: {
        make: regex,
        toValidationError: formatErrorDefault
    },
    customValidation: {
        make: customValidation,
        toValidationError: formatErrorDefault
    }
};

function toFormCtrlValidator(
    key: string,
    cnf: ValidationConfig,
    validationInfo: any,
    optionItems?: any
): ValidatorConfig {
    const validator = cnf.make(validationInfo, optionItems);
    return {
        message: formatErrorMessage(validationInfo, validator.message),
        validator: (ctrl: any) => {
            return validator.validate(ctrl.value)
                ? null
                : cnf.toValidationError(key, validationInfo);
        }
    };
}

type CustomErrorMessage = { errorMessage?: string };

function formatErrorMessage(
    validationInfo: CustomErrorMessage,
    defaultMessage: string
): string | null {
    return validationInfo.errorMessage || defaultMessage;
}

type FormValidationConfig = Record<
    string,
    {
        validator: (ctrl: FormControl) => ValidationErrors | null;
        message?: string;
    }
>;

export function toFormControlValidator(
    validationInfo: any,
    optionItems?: any
): FormValidationConfig {
    const cacheKey = JSON.stringify({ validationInfo, optionItems });
    if (cachedValidators.has(cacheKey)) {
        return cachedValidators.get(cacheKey);
    }

    const result = <any>Object.keys(validators).reduce(
        (accu, k) => ({
            ...accu,
            [k]: toFormCtrlValidator(
                k,
                validators[k],
                validationInfo,
                optionItems
            )
        }),
        {}
    );

    cachedValidators.set(cacheKey, result);
    return result;
}

function customValidation(validationInfo: any): ValueValidatorConfig {
    return {
        message: validationInfo.validations.customValidation?.errorMessage,
        validate: (val) => {
            if (!validationInfo.validations?.customValidation?.enable) {
                return true;
            }
            return validationInfo.validations.customValidation.validate(val);
        }
    };
}
